/**
 * LiveStepCounter — big animated count-up number with 45 s auto-refresh.
 * Used on the landing-page hero (umbrella Million Mile Movement counter)
 * and on individual event pages (per-event total).
 *
 * Props:
 *   url             API endpoint that returns { total_steps, total_miles, ... }
 *   label           Headline above the counter ("Million Mile Movement")
 *   sublabel        Caption below ("steps · miles")
 *   showMiles       Bool — show the miles equivalent alongside steps
 *   refreshMs       Polling interval (default 45_000 ms per user spec)
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function useCountUp(target, durationMs = 1200) {
  const [val, setVal] = useState(0);
  const startVal = useRef(0);
  useEffect(() => {
    const from = startVal.current;
    const to = target;
    if (from === to) return;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else startVal.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

export default function LiveStepCounter({
  url,
  label = "Million Mile Movement",
  sublabel,
  showMiles = true,
  refreshMs = 45_000,
  variant = "hero",
}) {
  const [data, setData] = useState({ total_steps: 0, total_miles: 0,
                                     participant_count: 0 });
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${BACKEND}${url}`);
        if (cancelled) return;
        setData(r.data);
        setPulse(true);
        setTimeout(() => setPulse(false), 700);
      } catch {
        /* keep last value */
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, refreshMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [url, refreshMs]);

  const steps = useCountUp(data.total_steps);
  const miles = useCountUp(Math.round(data.total_miles));

  const big = variant === "hero" ? "text-7xl sm:text-8xl" : "text-5xl";
  return (
    <div data-testid="live-step-counter" className="text-center">
      <p className="text-xs tracking-[0.4em] uppercase text-purple-200/90 font-bold">
        {label}
      </p>
      <div className={`mt-3 font-black tabular-nums leading-none ${big}
        text-transparent bg-clip-text bg-gradient-to-r from-purple-300
        via-pink-300 to-fuchsia-300 transition-transform duration-700
        ${pulse ? "scale-[1.02]" : "scale-100"}`}
      >
        {steps.toLocaleString()}
      </div>
      <p className="mt-2 text-sm text-purple-100/80">
        {sublabel || "steps logged across every GLPFlo step-a-thon"}
      </p>
      {showMiles && (
        <p className="mt-4 text-sm text-pink-200">
          ≈ <span className="font-bold tabular-nums">{miles.toLocaleString()}</span>{" "}
          miles · <span className="tabular-nums">{(data.participant_count || 0).toLocaleString()}</span>{" "}
          participants
        </p>
      )}
    </div>
  );
}
